// ============================================================
// Add Protocol v3 wizard - save assembly
//
// Converts a completed WizardState into the Protocol row + the new
// InventoryItem rows for saveProtocolWithVials (C4). ALL derived
// mixture numbers come from deriveMixtureFields in calculations.ts -
// the sole derivation writer (C7 acceptance criterion); this module
// only routes its outputs into the persisted shapes.
//
// Identity rules (Phase A contracts):
// - Each mix vial gets ONE fresh mixGroupId shared by its components
//   and its inventory row.
// - Combined components store THIS peptide's mg as vialSizeMg, the
//   MIXTURE concentration as concentrationMcgPerUnit, the shared
//   drawUnits, and the TRUE derived per-peptide doseMcg on the phase.
// - Approximate vials (C6) clamp supply features: totalDoses null.
// - Powder vials (C7) save as Stored (isResearching) with null totals;
//   the Start Using flow collects real reconstitution later.
// ============================================================

import { FrequencyPattern, SyringeType } from '../../../data/types';
import type {
  InventoryItem,
  Protocol,
  ProtocolComponent,
  ProtocolPhase,
  VialReconstitution,
} from '../../../data/types';
import {
  calculateConcentration,
  calculateDosesPerVial,
  deriveMixtureFields,
  unitsForDose,
} from '../../../utils/calculations';
import { uuid } from '../../../utils/uuid';
import { isApproximate, isPowderPath, recipeMg, singleDoseMcg, vialMg, waterMl } from './wizardReducer';
import type { WizardState, WizardVial } from './wizardTypes';

// ------------------------------------------------------------
// Dose resolution
// ------------------------------------------------------------

/** Normalized single-vial dose in mcg from S9 display state. */
// singleDoseMcg lives in wizardReducer (the reducer's phase-1 seeding
// needs the same mcg conversion - a second inline copy misread mL doses).
// Re-exported here so existing imports keep resolving.
export { singleDoseMcg } from './wizardReducer';

/**
 * The recipe that DRIVES dosing for a mix vial. For a vial linked to a
 * STORED combo this is ALWAYS the stored vial's real recipe
 * (`linkedVial.mixRecipe`) - never the editable typed recipe, which the
 * user can still change after linking (Back to s3b, or Edit-from-Review).
 * This is the single enforcement point for "the stored vial's numbers are
 * what count": the reducer overwriting `vial.recipe` on accept is
 * convenience; THIS is the guarantee that a later edit can't corrupt the
 * concentration written onto the physical vial.
 */
export function effectiveMixRecipe(
  vial: WizardVial,
): Array<{ peptideId: string; peptideName: string; amountMg: number }> {
  const stored = vial.linkedVial?.mixRecipe;
  if (vial.linkedVialId && stored && stored.length >= 2) {
    return stored.map((r) => ({ peptideId: r.peptideId, peptideName: r.peptideName, amountMg: r.amountMg }));
  }
  return recipeMg(vial);
}

/**
 * Resolve a mix vial's drawUnits from S9's two-card answer (D10):
 * either the draw itself (units or mL) or one peptide's mcg dose
 * converted through that peptide's share of the mixture.
 */
export function mixDrawUnits(vial: WizardVial): number {
  const raw = parseFloat(vial.doseText);
  if (!(raw > 0)) return 0;
  const water = waterMl(vial);
  const recipe = effectiveMixRecipe(vial);
  if (vial.doseMode === 'draw') {
    // 1 unit = 0.01 mL on every supported syringe (C2).
    return vial.doseUnit === 'ml' ? raw * 100 : raw;
  }
  // perPeptide: mcg of ONE peptide -> the draw that delivers it.
  const targetId = vial.dosePeptideId ?? recipe[0]?.peptideId;
  const target = recipe.find((r) => r.peptideId === targetId);
  if (!target || water <= 0) return 0;
  const doseMcg = vial.doseUnit === 'mg' ? raw * 1000 : raw;
  const componentConcentration = calculateConcentration(target.amountMg, water);
  return unitsForDose(doseMcg, componentConcentration);
}

// ------------------------------------------------------------
// Assembly
// ------------------------------------------------------------

export interface BuiltProtocol {
  protocol: Protocol;
  /** New inventory rows to insert AFTER the protocol row (C4). */
  vials: InventoryItem[];
  /** Existing stored combos to flip to in-use atomically with the protocol
   *  insert. Empty unless a reused mix starts now (storage-mix reuse). */
  reconstitutions: VialReconstitution[];
}

function makePhase(doseMcg: number): ProtocolPhase {
  return {
    id: uuid(),
    phaseNumber: 1,
    doseMcg,
    durationValue: null,
    durationUnit: null,
    isMaintenance: true,
    source: 'user_manual',
  };
}

function scheduleFields(vial: WizardVial): Pick<ProtocolComponent, 'frequency' | 'selectedDays' | 'timesOfDay' | 'mealTiming'> {
  return {
    frequency: vial.schedule.frequency ?? FrequencyPattern.DAILY,
    selectedDays: vial.schedule.selectedDays.length > 0 ? vial.schedule.selectedDays : undefined,
    timesOfDay: vial.schedule.timesOfDay.length > 0 ? vial.schedule.timesOfDay : ['08:00'],
    mealTiming: vial.schedule.mealTiming,
  };
}

function localDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function mixDisplayName(vial: WizardVial): string {
  const names = recipeMg(vial).map((r) => r.peptideName);
  return names.join(' + ');
}

/**
 * Build the Protocol + new inventory rows from a completed wizard
 * state. Throws on structurally-impossible states (unanswered
 * screens) - the Review screen never renders those, so a throw here
 * is a programming error, not a user path.
 */
export function buildProtocol(state: WizardState): BuiltProtocol {
  const now = new Date();
  const nowIso = now.toISOString();
  // Start timing. A chosen future date => a Scheduled protocol; the dateless
  // "start later" toggle => a Paused protocol; neither => Active today.
  const scheduled = !!state.startDate;
  // "Not starting now" covers both dormant paths. New tracked vials are then
  // parked in storage (isResearching, no counts, clock deferred to activation)
  // so a dormant protocol never burns supply or fires expiry alerts - this is
  // the fix for the start-later liquid-vial-saved-active bug.
  const notStartingNow = scheduled || state.startLater;
  // Fail closed (codex P2-11 / D15): the syringe is a real answer,
  // never an assumption. No legit path reaches Review without S8.
  const syringe = state.syringeType;
  if (!syringe) throw new Error('syringe type not answered');
  const components: ProtocolComponent[] = [];
  const newVials: InventoryItem[] = [];
  const reconstitutions: VialReconstitution[] = [];

  for (const vial of state.vials) {
    const water = waterMl(vial);
    const powder = isPowderPath(vial);
    const approximate = isApproximate(vial);
    // A reused SEALED stored combo (no stored water) is physically powder
    // even though the wizard path isn't - it must also wait for a real mix
    // at activation (eng review OV3).
    const linkedSealed = !!vial.linkedVialId && (vial.linkedVial?.bacWaterMl ?? 0) <= 0;
    // Persisted on components so the activation engine can tell "prompt the
    // user to mix" (unmixed at save) apart from "auto-activate" (already a
    // liquid) - parked inventory rows are otherwise identical.
    const needsMixOnStart = notStartingNow && (powder || linkedSealed);

    if (vial.kind === 'mix') {
      // For a reused stored combo this is the STORED recipe, not the typed
      // one - the guarantee that an edited recipe can't poison the vial.
      const recipe = effectiveMixRecipe(vial);
      if (recipe.length < 2) throw new Error('mix vial without a valid recipe');
      const drawUnits = mixDrawUnits(vial);
      if (!(drawUnits > 0)) throw new Error('mix vial without a valid dose');
      const derived = deriveMixtureFields(recipe, water, drawUnits, syringe);
      // A reused stored combo keeps its OWN mix group id so the components
      // still share the vial's identity (matching the fresh-vial invariant
      // that a mix vial's group id equals its components'). A brand-new mix
      // mints one.
      const mixGroupId = vial.linkedVialId
        ? (vial.linkedVial?.mixGroupId ?? uuid())
        : uuid();

      // Inventory row first so components can link to it.
      let inventoryId: string | undefined;
      if (vial.trackVial && !vial.linkedVialId) {
        inventoryId = uuid();
        const totalMg = recipe.reduce((sum, r) => sum + r.amountMg, 0);
        const expDate = new Date(now);
        expDate.setDate(expDate.getDate() + 28);
        newVials.push({
          id: inventoryId,
          peptideId: recipe[0].peptideId,
          peptideName: mixDisplayName(vial),
          vialSizeMg: totalMg,
          bacWaterMl: water,
          concentrationMcgPerUnit: derived.concentrationMcgPerUnit,
          dateReconstituted: localDateStr(now),
          expirationDate: localDateStr(expDate),
          // C6/C7: approximate + powder vials clamp supply counts. A vial for
          // a not-starting-now protocol is parked in storage with null counts
          // (clock + counts stamped at activation).
          totalDoses: powder || approximate || notStartingNow ? null : derived.totalDoses,
          dosesUsed: 0,
          dosesRemaining: powder || approximate || notStartingNow ? null : derived.totalDoses,
          isActive: true,
          isResearching: powder || notStartingNow,
          mixGroupId,
          mixDisplayName: mixDisplayName(vial),
          mixRecipe: recipe,
          createdAt: nowIso,
        });
      } else if (vial.linkedVialId) {
        inventoryId = vial.linkedVialId;
        // Reusing a STORED combo that starts now: flip it to in-use in the
        // same atomic write as the protocol insert. BOTH dormant paths
        // (scheduled and the dateless pause) leave it stored - activation
        // reconstitutes it later (D6). Gating on startLater alone made every
        // scheduled protocol reusing a stored combo fail at save: the save
        // RPC rejects reconstitutions on a non-active protocol (review T1).
        // The counts are the REAL derived ones, never the powder null-clamp:
        // the user gave us real water (stored, or entered at S7).
        if (!notStartingNow) {
          // A SEALED combo (no stored water) is being mixed for the first
          // time now, so restart its 28-day clock (mirrors Start Using).
          // An already-mixed combo keeps the clock it started when mixed.
          const sealed = linkedSealed;
          const reconExp = new Date(now);
          reconExp.setDate(reconExp.getDate() + 28);
          reconstitutions.push({
            inventoryId: vial.linkedVialId,
            bacWaterMl: water,
            concentrationMcgPerUnit: derived.concentrationMcgPerUnit,
            totalDoses: derived.totalDoses,
            dosesRemaining: derived.totalDoses,
            ...(sealed ? { dateReconstituted: localDateStr(now), expirationDate: localDateStr(reconExp) } : {}),
            mixGroupId,
            peptideIds: recipe.map((r) => r.peptideId),
          });
        }
      }

      for (const part of derived.perComponent) {
        components.push({
          id: uuid(),
          peptideId: part.peptideId,
          peptideName: part.peptideName,
          vialSizeMg: part.amountMg,
          bacWaterMl: water,
          syringeType: syringe,
          // The MIXTURE concentration - unitsForDose(groupTotalMcg, this)
          // reproduces drawUnits (Phase A group-schedule fallback).
          concentrationMcgPerUnit: derived.concentrationMcgPerUnit,
          inventoryId,
          mixGroupId,
          ...(needsMixOnStart ? { requiresMixOnStart: true, mixGuide: state.wantsMixGuide !== false } : {}),
          ...(approximate ? { approximate: true } : {}),
          drawUnits,
          ...scheduleFields(vial),
          phases: [makePhase(part.doseMcg)],
        });
      }
      continue;
    }

    // ---- single vial ----
    if (!vial.peptideId || !vial.peptideName) throw new Error('single vial without a peptide');
    const mg = vialMg(vial);
    const doseMcg = singleDoseMcg(vial);
    if (!(mg > 0) || !(doseMcg > 0)) throw new Error('single vial without valid amounts');
    const concentration = calculateConcentration(mg, water);

    let inventoryId: string | undefined;
    if (vial.linkedVialId) {
      inventoryId = vial.linkedVialId;
    } else if (vial.trackVial) {
      inventoryId = uuid();
      // Parked in storage (null counts) when the protocol isn't starting now.
      const totalDoses = powder || approximate || notStartingNow ? null : calculateDosesPerVial(mg, doseMcg) || null;
      const expDate = new Date(now);
      expDate.setDate(expDate.getDate() + 28);
      newVials.push({
        id: inventoryId,
        peptideId: vial.peptideId,
        peptideName: vial.peptideName,
        vialSizeMg: mg,
        bacWaterMl: water,
        concentrationMcgPerUnit: concentration,
        dateReconstituted: localDateStr(now),
        expirationDate: localDateStr(expDate),
        totalDoses,
        dosesUsed: 0,
        dosesRemaining: totalDoses,
        isActive: true,
        isResearching: powder || notStartingNow,
        createdAt: nowIso,
      });
    }

    // S10 step-up phases (PhasesStep output) or the steady S9 dose.
    const phases = vial.doseChanges && vial.phases && vial.phases.length > 0
      ? vial.phases
      : [makePhase(doseMcg)];

    components.push({
      id: uuid(),
      peptideId: vial.peptideId,
      peptideName: vial.peptideName,
      vialSizeMg: mg,
      bacWaterMl: water,
      syringeType: syringe,
      concentrationMcgPerUnit: concentration,
      inventoryId,
      ...(needsMixOnStart ? { requiresMixOnStart: true, mixGuide: state.wantsMixGuide !== false } : {}),
      ...(approximate ? { approximate: true } : {}),
      ...scheduleFields(vial),
      phases,
    });
  }

  const autoName = state.vials
    .map((v) => (v.kind === 'mix' ? `${mixDisplayName(v)} combo` : v.peptideName ?? 'Peptide'))
    .join(' + ');

  // A future date => Scheduled (auto-activates on that day). The dateless
  // "start later" toggle => Paused (activate manually). Neither => Active.
  // All three stay off the dashboard/reminders until active (is_active=false
  // for scheduled + paused).
  const status: Protocol['status'] = scheduled ? 'scheduled' : state.startLater ? 'paused' : 'active';
  const protocol: Protocol = {
    id: uuid(),
    name: state.protocolName.trim() || autoName,
    status,
    isActive: status === 'active',
    // Scheduled keeps the chosen future date; everything else starts today.
    startDate: scheduled ? state.startDate! : localDateStr(now),
    // Persisted so activation knows whether to create dose reminders later.
    notifyEachDose: state.notifyEachDose,
    components,
    createdAt: nowIso,
    updatedAt: nowIso,
  };

  return { protocol, vials: newVials, reconstitutions };
}
